/**
 * Manual Withdraw Page
 *
 * Fallback page for manually claiming deposits when the main dashboard loses track.
 * Two-step flow:
 * 1. Enter deposit TX hash, select source chain, enter deposit amount
 * 2. Configure withdrawal requests (similar to main dashboard dialog)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Card,
    CardContent,
    Button,
    TextField,
    Alert,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    LinearProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputAdornment,
    Stack,
    Divider,
} from '@mui/material';
import {
    AccountBalanceWallet,
    CheckCircle,
    Error as ErrorIcon,
    Key,
    Refresh,
    Add,
    Remove,
    ArrowBack,
    Download,
    ContentCopy,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import KeyEncryptionService from '../utils/keyEncryption';

import { useWallet } from '../hooks/useWallet';
import { useThemeMode } from '../hooks/useThemeMode';
import { useWallet as useSolWallet } from '@solana/wallet-adapter-react';
import { useTronWallet } from '../hooks/useTronWallet';
import { getV2Status } from '../services/dkgKeyshareService';
import {
    requestKeysOnly,
    getAvailableDenominations,
} from '../services/dkgSimpleWithdrawal';

// DKG node URLs for fetching chains
const DKG_NODE_URLS = [
    process.env.REACT_APP_DKG_NODE_1 || 'http://localhost:8081',
    process.env.REACT_APP_DKG_NODE_2 || 'http://localhost:8082',
    process.env.REACT_APP_DKG_NODE_3 || 'http://localhost:8083',
];

function WithdrawV2() {
    const { connected, account, signer, connectWallet } = useWallet();
    const { isDark } = useThemeMode();
    const solanaWallet = useSolWallet();
    const { publicKey: solanaPublicKey, connected: solanaConnected } = solanaWallet;
    // Tron wallet — used ONLY when sourceChain is a Tron chain.
    const tronWallet = useTronWallet();
    const { address: tronAddress, connected: tronConnected, connect: connectTron } = tronWallet;

    // State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Two-step flow: 'input' or 'configure'
    const [step, setStep] = useState('input');

    // V2 Node status
    const [nodeStatuses, setNodeStatuses] = useState([]);
    const [v2Ready, setV2Ready] = useState(false);

    // Step 1: Initial inputs
    const [depositTxHash, setDepositTxHash] = useState('');
    const [sourceChain, setSourceChain] = useState('sepolia');
    const [depositAmount, setDepositAmount] = useState('');
    const [depositToken, setDepositToken] = useState('ETH');

    // Chain data
    const [supportedChains, setSupportedChains] = useState([]);
    const [_availableDenominations, setAvailableDenominations] = useState([]);

    // Step 2: Withdrawal configuration (like Dashboard dialog)
    const [withdrawalRequests, setWithdrawalRequests] = useState([
        { denomination: '', chain: 'sepolia', token: 'ETH' }
    ]);

    // Progress
    const [progress, setProgress] = useState(null);

    // Received keys storage
    const [receivedKeys, setReceivedKeys] = useState([]);

    // Password dialog for key download
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [downloadPassword, setDownloadPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [selectedKeyForDownload, setSelectedKeyForDownload] = useState(null); // null = all keys

    // Load chains from backend
    const loadChains = useCallback(async () => {
        for (const nodeUrl of DKG_NODE_URLS) {
            try {
                const response = await fetch(`${nodeUrl}/api/cross-chain/chains`);
                if (!response.ok) continue;
                const data = await response.json();
                if (data.success && data.chains) {
                    setSupportedChains(data.chains);
                    return;
                }
            } catch (err) {
                continue;
            }
        }
        // Fallback
        setSupportedChains([
            { chain_name: 'sepolia', name: 'Sepolia', native_currency: 'ETH' },
            { chain_name: 'mainnet', name: 'Mainnet', native_currency: 'ETH' },
        ]);
    }, []);

    // Load node status
    const checkV2Nodes = useCallback(async () => {
        try {
            const statuses = await getV2Status();
            setNodeStatuses(statuses);
            const healthyNodes = statuses.filter(s => s.healthy && s.masterInitialized);
            setV2Ready(healthyNodes.length >= 3);
        } catch (err) {
            console.error('Failed to check V2 nodes:', err);
            setV2Ready(false);
        }
    }, []);

    // Load denominations when chain changes
    const loadDenominations = useCallback(async (chainName) => {
        try {
            const denoms = await getAvailableDenominations(chainName);
            setAvailableDenominations(denoms);
        } catch (err) {
            console.error('Failed to load denominations:', err);
        }
    }, []);

    useEffect(() => {
        checkV2Nodes();
        loadChains();
    }, [checkV2Nodes, loadChains]);

    useEffect(() => {
        loadDenominations(sourceChain);
    }, [sourceChain, loadDenominations]);

    // Step 1: Continue to configuration
    const handleContinue = () => {
        if (!depositTxHash || depositTxHash.length < 64) {
            setError('Please enter a valid deposit transaction hash');
            return;
        }
        if (!depositAmount || parseFloat(depositAmount) <= 0) {
            setError('Please enter the deposit amount');
            return;
        }
        setError('');
        // Initialize withdrawal requests with deposit info
        setWithdrawalRequests([{
            denomination: '',
            chain: sourceChain,
            token: depositToken
        }]);
        setStep('configure');
    };

    // Go back to step 1
    const handleBack = () => {
        setStep('input');
        setProgress(null);
        setError('');
        setSuccess('');
    };

    // Withdrawal request helpers
    const addWithdrawalRequest = () => {
        const lastRequest = withdrawalRequests[withdrawalRequests.length - 1];
        setWithdrawalRequests([...withdrawalRequests, {
            denomination: '',
            chain: lastRequest?.chain || sourceChain,
            token: lastRequest?.token || depositToken
        }]);
    };

    const removeWithdrawalRequest = (index) => {
        if (withdrawalRequests.length > 1) {
            setWithdrawalRequests(withdrawalRequests.filter((_, i) => i !== index));
        }
    };

    const updateWithdrawalRequest = (index, field, value) => {
        const updated = [...withdrawalRequests];
        updated[index][field] = value;

        // When chain changes, update token to chain's native token
        if (field === 'chain') {
            const selectedChain = supportedChains.find(c => c.chain_name === value);
            if (selectedChain) {
                const chainType = selectedChain.chain_type || 'evm';
                if (chainType === 'solana') {
                    updated[index].token = 'SOL';
                } else {
                    updated[index].token = selectedChain.native_currency || 'ETH';
                }
            }
        }

        setWithdrawalRequests(updated);
    };

    // Calculate total requested
    const totalRequested = withdrawalRequests.reduce(
        (sum, r) => sum + (parseFloat(r.denomination) || 0), 0
    );

    // Execute key request
    const handleRequestKeys = async () => {
        const validRequests = withdrawalRequests
            .filter(req => req.denomination && parseFloat(req.denomination) > 0)
            .map(req => ({
                target_chain: req.chain,
                token_symbol: req.token,
                denomination: req.denomination.toString(),
            }));

        if (validRequests.length === 0) {
            setError('Please enter at least one valid amount');
            return;
        }

        // Validate total
        if (totalRequested > parseFloat(depositAmount)) {
            setError(`Total requested (${totalRequested}) exceeds deposit amount (${depositAmount})`);
            return;
        }

        setLoading(true);
        setError('');
        setProgress({ step: 'starting', message: 'Starting key request...' });

        try {
            // Detect source-chain type. Three-way routing enforces chain isolation —
            // each source chain validates against ONLY its own wallet.
            const sourceChainConfig = supportedChains.find(c => c.chain_name === sourceChain);
            const isSolanaChain = sourceChainConfig?.chain_type === 'solana' || sourceChain.includes('solana');
            const isTronChain = sourceChainConfig?.chain_type === 'tron' || sourceChain.startsWith('tron');

            // Validate correct wallet is connected
            if (isSolanaChain) {
                if (!solanaConnected || !solanaPublicKey) {
                    setError('Please connect your Phantom wallet for Solana chains');
                    setLoading(false);
                    return;
                }
            } else if (isTronChain) {
                if (!tronConnected || !tronAddress) {
                    try { await connectTron(); } catch (e) {
                        setError('Please connect your TronLink wallet for Tron chains');
                        setLoading(false);
                        return;
                    }
                }
            } else {
                if (!signer) {
                    setError('Please connect your MetaMask wallet');
                    setLoading(false);
                    return;
                }
            }

            const result = await requestKeysOnly({
                depositTxHash: depositTxHash.trim(),
                sourceChain,
                withdrawalRequests: validRequests,
                // ONLY pass the wallet matching the source chain; all others null.
                signer: (isSolanaChain || isTronChain) ? null : signer,
                solanaWallet: isSolanaChain ? solanaWallet : null,
                tronWallet: isTronChain ? tronWallet : null,
                isSolana: isSolanaChain,
                isTron: isTronChain,
            }, (prog) => {
                setProgress(prog);
            });

            if (result.success) {
                setSuccess(`Successfully received ${result.keys.length} key(s)! Total value: ${result.total_value} ${depositToken}. Download your keys below.`);
                toast.success('Keys received successfully!');

                // Store received keys with deposit context
                const keysWithContext = result.keys.map(key => ({
                    ...key,
                    depositTxHash: depositTxHash.trim(),
                    sourceChain,
                    depositToken,
                    depositAmount,
                    receivedAt: new Date().toISOString(),
                }));
                setReceivedKeys(keysWithContext);
                console.log('Received keys:', keysWithContext);
            } else {
                setError(result.error || 'Key request failed');
                toast.error('Key request failed: ' + result.error);
            }
        } catch (err) {
            console.error('Key request error:', err);
            setError(err.message);
            toast.error('Error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Open password dialog for downloading keys
    const handleDownloadKey = (keyIndex = null) => {
        setSelectedKeyForDownload(keyIndex);
        setDownloadPassword('');
        setPasswordDialog(true);
    };

    // Download keys with encryption
    const downloadKeysWithPassword = async () => {
        if (!downloadPassword || downloadPassword.length < 4) {
            toast.error('Password must be at least 4 characters');
            return;
        }

        try {
            // Filter keys - check both keyIndex and key_index since format varies
            const keysToDownload = selectedKeyForDownload !== null
                ? receivedKeys.filter(k => (k.keyIndex || k.key_index) === selectedKeyForDownload)
                : receivedKeys;

            if (keysToDownload.length === 0) {
                toast.error('No keys to download');
                return;
            }

            // Prepare key data for download - MUST include isSingleNode for Dashboard compatibility
            const keysData = {
                depositId: depositTxHash,
                requestId: null,
                isSingleNode: true, // REQUIRED for Dashboard upload to work
                isSingleKey: keysToDownload.length === 1,
                sourceChain,
                depositToken,
                depositAmount,
                keys: keysToDownload.map(key => ({
                    keyIndex: key.keyIndex || key.key_index,
                    private_key: key.private_key,
                    ethereum_address: key.ethereum_address,
                    solana_address: key.solana_address,
                    denomination: key.denomination,
                    denomination_wei: key.denomination_wei,
                    merkleRoot: key.merkleRoot || key.merkle_root,
                    merkle_root_id: key.merkle_root_id ?? key.merkleRootId,
                    merkleProof: key.merkleProof || key.merkle_proof,
                    treeIndex: key.treeIndex || key.tree_index,
                    batchId: key.batchId || key.merkle_root_id,
                    userAddress: key.userAddress,
                    chain_name: key.chain_name || sourceChain,
                    token_symbol: key.token_symbol || depositToken,
                    isUsed: key.isUsed || false
                })),
                timestamp: new Date().toISOString()
            };

            // Encrypt with password using KeyEncryptionService
            const encryptedData = KeyEncryptionService.encryptKeys(keysData, downloadPassword);

            // Create download
            const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
            const filename = selectedKeyForDownload !== null
                ? `treasury-key-${depositTxHash.slice(0, 10)}-index${selectedKeyForDownload}-${timestamp}.enc`
                : `treasury-keys-${depositTxHash.slice(0, 10)}-${timestamp}.enc`;

            KeyEncryptionService.downloadEncryptedKeys(encryptedData, filename);

            toast.success('Keys downloaded successfully! Keep your password safe.');
            setPasswordDialog(false);
            setDownloadPassword('');
        } catch (err) {
            console.error('Download error:', err);
            toast.error('Failed to download keys: ' + err.message);
        }
    };

    // Copy private key to clipboard
    const copyPrivateKey = async (privateKey) => {
        try {
            await navigator.clipboard.writeText(privateKey);
            toast.success('Private key copied to clipboard');
        } catch (err) {
            toast.error('Failed to copy to clipboard');
        }
    };

    // Get tokens for a chain — strictly from backend supported_tokens
    const getTokensForChain = (chainName) => {
        const chain = supportedChains.find(c => c.chain_name === chainName);
        if (!chain || !chain.supported_tokens || chain.supported_tokens.length === 0) return [];
        return chain.supported_tokens;
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {step === 'configure' && (
                        <IconButton onClick={handleBack} size="small">
                            <ArrowBack />
                        </IconButton>
                    )}
                    <Typography variant="h4">
                        Manual Withdraw
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {step === 'input'
                        ? 'Use this page to manually claim a deposit if the main dashboard lost track of it.'
                        : 'Configure your withdrawal requests. You can split across multiple chains/tokens.'}
                </Typography>

                {/* DKG Node Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">
                        DKG Nodes ({nodeStatuses.filter(s => s.healthy && s.masterInitialized).length}/5 ready):
                    </Typography>
                    <Button size="small" onClick={checkV2Nodes} disabled={loading}>
                        <Refresh fontSize="small" />
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {nodeStatuses.length > 0 ? (
                        nodeStatuses.map((status, i) => (
                            <Chip
                                key={i}
                                size="small"
                                variant="outlined"
                                icon={status.healthy && status.masterInitialized ? <CheckCircle /> : <ErrorIcon />}
                                label={`Node ${status.nodeId || i + 1}`}
                                color={status.healthy && status.masterInitialized ? 'success' : 'error'}
                                sx={{ height: 24, fontSize: '0.7rem' }}
                            />
                        ))
                    ) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                            Checking nodes...
                        </Typography>
                    )}
                </Box>
                {!v2Ready && nodeStatuses.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Requires at least 3 nodes with initialized master seed
                    </Alert>
                )}
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Main Content */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    {!connected ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Connect your wallet to continue
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AccountBalanceWallet />}
                                onClick={connectWallet}
                            >
                                Connect Wallet
                            </Button>
                        </Box>
                    ) : step === 'input' ? (
                        /* Step 1: Initial Input */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                Connected: {account}
                            </Typography>

                            <TextField
                                fullWidth
                                label="Deposit Transaction Hash"
                                value={depositTxHash}
                                onChange={(e) => setDepositTxHash(e.target.value)}
                                placeholder="0x..."
                                helperText="The transaction hash of your deposit to the treasury"
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl sx={{ minWidth: 150 }}>
                                    <InputLabel>Source Chain</InputLabel>
                                    <Select
                                        value={sourceChain}
                                        label="Source Chain"
                                        onChange={(e) => setSourceChain(e.target.value)}
                                    >
                                        {supportedChains.map(chain => (
                                            <MenuItem key={chain.chain_name} value={chain.chain_name}>
                                                {chain.name || chain.chain_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Deposit Amount"
                                    value={depositAmount}
                                    onChange={(e) => setDepositAmount(e.target.value)}
                                    placeholder="0.001"
                                    type="number"
                                    inputProps={{ step: '0.001', min: '0' }}
                                    sx={{ flex: 1 }}
                                />

                                <FormControl sx={{ minWidth: 100 }}>
                                    <InputLabel>Token</InputLabel>
                                    <Select
                                        value={depositToken}
                                        label="Token"
                                        onChange={(e) => setDepositToken(e.target.value)}
                                    >
                                        {getTokensForChain(sourceChain).map(token => (
                                            <MenuItem key={token} value={token}>{token}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleContinue}
                                disabled={!v2Ready || !depositTxHash || !depositAmount}
                            >
                                Continue
                            </Button>
                        </Box>
                    ) : (
                        /* Step 2: Configure Withdrawals */
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Deposit Info */}
                            <Box sx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)', p: 2, borderRadius: 1 }}>
                                <Typography variant="body2">
                                    <strong>Deposit TX:</strong>
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {depositTxHash}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Amount:</strong> {depositAmount} {depositToken} on {sourceChain}
                                </Typography>
                            </Box>

                            {/* Withdrawal Requests */}
                            <Typography variant="subtitle2">
                                Withdrawal Requests
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }}>
                                You can request multiple keys (e.g., deposit 2 ETH, withdraw 1 ETH on Base + 1 ETH on Polygon)
                            </Typography>

                            {withdrawalRequests.map((request, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <TextField
                                        label={`Amount ${index + 1}`}
                                        value={request.denomination}
                                        onChange={(e) => updateWithdrawalRequest(index, 'denomination', e.target.value)}
                                        placeholder="0.001"
                                        type="number"
                                        inputProps={{ step: '0.001', min: '0' }}
                                        sx={{ flex: '0 0 100px' }}
                                        size="small"
                                        disabled={loading}
                                    />
                                    <FormControl sx={{ flex: '0 0 100px' }} size="small">
                                        <InputLabel>Token</InputLabel>
                                        <Select
                                            value={request.token}
                                            onChange={(e) => updateWithdrawalRequest(index, 'token', e.target.value)}
                                            label="Token"
                                            disabled={loading}
                                        >
                                            {getTokensForChain(request.chain).map(token => (
                                                <MenuItem key={token} value={token}>{token}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ flex: 1 }} size="small">
                                        <InputLabel>Target Chain</InputLabel>
                                        <Select
                                            value={request.chain}
                                            onChange={(e) => updateWithdrawalRequest(index, 'chain', e.target.value)}
                                            label="Target Chain"
                                            disabled={loading}
                                        >
                                            {supportedChains.map(chain => (
                                                <MenuItem key={chain.chain_name} value={chain.chain_name}>
                                                    {chain.name || chain.chain_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {withdrawalRequests.length > 1 && (
                                            <IconButton
                                                onClick={() => removeWithdrawalRequest(index)}
                                                color="error"
                                                size="small"
                                                disabled={loading}
                                            >
                                                <Remove />
                                            </IconButton>
                                        )}
                                        {index === withdrawalRequests.length - 1 && (
                                            <IconButton
                                                onClick={addWithdrawalRequest}
                                                color="primary"
                                                size="small"
                                                disabled={loading}
                                            >
                                                <Add />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            ))}

                            {/* Total */}
                            <Typography variant="caption" sx={{
                                color: totalRequested > parseFloat(depositAmount) ? '#ef4444' : 'rgba(255, 255, 255, 0.5)'
                            }}>
                                Total: {totalRequested.toFixed(4)} / {depositAmount} {depositToken}
                            </Typography>

                            {/* Progress */}
                            {progress && (
                                <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} />
                                        <Typography variant="body2">
                                            {progress.message}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {/* How it works */}
                            <Alert severity="info">
                                <Typography variant="body2">
                                    <strong>How it works:</strong>
                                </Typography>
                                <Typography variant="body2" component="div">
                                    <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
                                        <li>Key index computed from your deposit TX (deterministic)</li>
                                        <li>Sign a proof message with MetaMask</li>
                                        <li>Receive keyshares from 3+ DKG nodes</li>
                                        <li>Private key reconstructed locally (never leaves browser)</li>
                                        <li>Sign and submit withdrawal transaction</li>
                                    </ol>
                                </Typography>
                            </Alert>

                            {/* Request Button */}
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleRequestKeys}
                                disabled={loading || !v2Ready || totalRequested <= 0 || totalRequested > parseFloat(depositAmount)}
                                startIcon={loading ? <CircularProgress size={20} /> : <Key />}
                            >
                                {loading ? 'Requesting...' : 'Request Keys'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Received Keys Display */}
            {receivedKeys.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Key /> Received Keys ({receivedKeys.length})
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={() => handleDownloadKey(null)}
                            sx={{
                                background: isDark ? '#ffffff' : '#171717', color: isDark ? '#050505' : '#ffffff',
                            }}
                        >
                            Download All Keys
                        </Button>
                    </Box>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Important:</strong> Download and save your keys now. They will be lost if you refresh the page.
                        Use the "Upload Keys" feature in the main Dashboard to use these keys later.
                    </Alert>

                    <Stack spacing={2}>
                        {receivedKeys.map((key, index) => (
                            <Card key={index} sx={{ bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)', border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)' }}>
                                <CardContent sx={{ py: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                        <Box sx={{ flex: 1, minWidth: 200 }}>
                                            <Typography variant="subtitle2" sx={{ color: isDark ? '#e5e5e5' : '#171717' }}>
                                                Key #{key.keyIndex || key.key_index}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                <strong>Amount:</strong> {key.denomination} {key.token_symbol || depositToken}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Chain:</strong> {key.chain_name || sourceChain}
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)', display: 'block', mt: 0.5 }}>
                                                Address: {key.ethereum_address?.slice(0, 10)}...{key.ethereum_address?.slice(-8)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<ContentCopy />}
                                                onClick={() => copyPrivateKey(key.private_key)}
                                                sx={{
                                                    borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                    '&:hover': {
                                                        borderColor: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                                    },
                                                }}
                                            >
                                                Copy Key
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Download />}
                                                onClick={() => handleDownloadKey(key.keyIndex || key.key_index)}
                                                sx={{
                                                    borderColor: isDark ? '#404040' : '#d4d4d4',
                                                    color: isDark ? '#e5e5e5' : '#171717',
                                                    '&:hover': {
                                                        borderColor: isDark ? '#e5e5e5' : '#171717',
                                                        bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
                                                    },
                                                }}
                                            >
                                                Save Key
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Alert severity="info">
                        <Typography variant="body2">
                            <strong>Next Steps:</strong>
                        </Typography>
                        <Typography variant="body2" component="div">
                            <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
                                <li>Download your keys using the buttons above</li>
                                <li>Go to the main Dashboard (Manage Keys)</li>
                                <li>Click "Upload Keys" to import your saved key file</li>
                                <li>Use the imported keys to complete your withdrawal</li>
                            </ol>
                        </Typography>
                    </Alert>
                </Paper>
            )}

            {/* Password Dialog for Download */}
            <Dialog
                open={passwordDialog}
                onClose={() => setPasswordDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {selectedKeyForDownload !== null
                        ? `Encrypt Key #${selectedKeyForDownload} for Saving`
                        : 'Encrypt All Keys for Saving'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Enter a password to encrypt your keys. You will need this password to decrypt and use the keys later.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Encryption Password"
                        type={showPassword ? 'text' : 'password'}
                        value={downloadPassword}
                        onChange={(e) => setDownloadPassword(e.target.value)}
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && downloadPassword.length >= 4) {
                                downloadKeysWithPassword();
                            }
                        }}
                    />
                    <Typography variant="caption" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)', mt: 1, display: 'block' }}>
                        Minimum 4 characters. Keep this password safe - you will need it to use these keys.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={downloadKeysWithPassword}
                        disabled={downloadPassword.length < 4}
                        startIcon={<Download />}
                    >
                        Download Encrypted
                    </Button>
                </DialogActions>
            </Dialog>

            {loading && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
                    <LinearProgress />
                </Box>
            )}
        </Box>
    );
}

export default WithdrawV2;
